import { flow, types } from 'mobx-state-tree'
import { APIEndPoints } from '../../network/APIEndpoints'
import { bulkFileDownloadReq } from '../../network/bulkFileDownloadReq'
import { bulkRefundProcessReq } from '../../network/bulkRefundProcessReq'
import { bulkRefundUploadConfirmReq } from '../../network/bulkRefundUploadConfirm'
import { extendAppSessionAPI } from '../../network/extendAppSession'
import { extendUserSessionAPI } from '../../network/extendUserSession'
import { getBulkSummary } from '../../network/getBulkSummary'
import { sampleBulkFileDownloadReq } from '../../network/sampleBulkFileDownloadReq'
import { uploadFileReq } from '../../network/uploadFileReq'
import { bulkRefundErrorMessage, downloadFile, getDateInyyyymmdd, getSelectedDate } from '../../util/util'
import { bulkSummaryTableParse } from '../parser/view/TableParser'
import { mimeTypes, paginationSettings } from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { Pagination } from './Transaction'

const BulkReport = types.model('BulkReport', {
  id: types.optional(types.string, ''),
  referenceNumber: types.optional(types.string, ''),
  transactionAmount: types.optional(types.number, 0),
  refundAmount: types.optional(types.number, 0),
})

const Summary = types.model('Summary', {
  id: types.optional(types.string, ''),
  fileName: types.optional(types.string, ''),
  createdAt: types.optional(types.string, ''),
  count: types.optional(types.number, 0),
  totalRefundAmount: types.optional(types.number, 0),
  accepted: types.optional(types.number, 0),
  successAmount: types.optional(types.number, 0),
  declined: types.optional(types.number, 0),
  failureAmount: types.optional(types.number, 0),
  updatedAt: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
})

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.string,
})

const BulkRefund = types
  .model('BulkRefund', {
    report: types.array(BulkReport),
    summary: types.array(Summary),
    pagination: Pagination,
    uploadStatus: types.optional(
      types.enumeration(['NOT_SELECTED', 'IN_PROGRESS', 'UPLOADED', 'IN_PROCESS', 'PROCESSED']),
      'NOT_SELECTED',
    ),
    count: types.maybeNull(types.number),
    size: types.maybeNull(types.number),
    uploadedFileId: types.optional(types.number, -1),
    selectedDateInfo: DateRange,
  })
  .actions((self) => ({
    setSize: (size: number | null) => {
      self.size = size
    },
    setCount: (count: number | null) => {
      self.count = count
    },
    setUploadStatus: (
      status: 'NOT_SELECTED' | 'IN_PROGRESS' | 'UPLOADED' | 'IN_PROCESS' | 'PROCESSED' = 'NOT_SELECTED',
    ) => {
      self.uploadStatus = status
    },
    sampleBulkFileDownload: flow(function* sampleBulkFileDownload(mid: string) {
      let resp
      let errorCode = ''
      let isFileDownloading = true
      try {
        resp = yield sampleBulkFileDownloadReq(mid)
        try {
          const errorFileResponse =
            resp?.data?.status !== 'SUCCESS' && JSON.parse(Buffer.from(resp?.data).toString('utf8'))
          errorCode = errorFileResponse?.error?.code ?? ''
          if (errorCode === '33607' || errorCode === '33306') {
            if (errorCode === '33607') {
              extendUserSessionAPI().then((response) => {
                console.log('extendUserSessionAPI response', response)
                return sampleBulkFileDownload(mid)
              })
            } else {
              extendAppSessionAPI().then((response) => {
                console.log('extendAppSessionAPI response', response)
                return sampleBulkFileDownload(mid)
              })
            }
          }
        } catch (e) {
          console.log({ e })
        }
        if (window && errorCode === '' && resp && resp?.data) {
          console.log(resp)
          console.log(resp.headers['content-type'])
          let type = resp.headers['content-type']
          //check for undefined file download on session expire
          if (mimeTypes.get(type) === undefined) {
            return
          }
          const blob = new Blob([resp?.data], {
            type,
          })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.href = url
          a.download = 'SampleBulkRefundFile.xls' //`${fileName || `file${mimeTypes.get(type)}`}`//TODO: check do we need to hard code file name
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
          isFileDownloading = false
        }
      } catch (error) {
        isFileDownloading = true
        console.log('error block' + JSON.stringify(error))
        throw error
      }
      return isFileDownloading
    }),
    updateSelectedDateInfo(dateObj: any): void {
      self.selectedDateInfo.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      self.selectedDateInfo.toDate = dateObj?.toDate ?? new Date(Date.now())
      self.selectedDateInfo.position.top = dateObj?.position?.top ?? 0
      self.selectedDateInfo.position.left = dateObj?.position?.left ?? 0
      self.selectedDateInfo.position.visible = dateObj?.position?.visible ?? false
      self.selectedDateInfo.dateLabel = dateObj?.dateLabel ?? ''
    },
    fetchBulkSummary: flow(function* fetchBulkSummary(
      mid: string,
      fileNameQuery: string,
      pageNumber: number,
      pageSize: number,
      _isLoadMore = false,
    ) {
      const own = self
      try {
        const isCustomRange = self.selectedDateInfo.dateLabel === 'Custom Range'
        const isYesterday = self.selectedDateInfo.dateLabel === 'Yesterday'
        const toDate = getDateInyyyymmdd(
          isCustomRange || isYesterday ? self.selectedDateInfo.toDate : new Date(Date.now()),
        )
        const fromDate = getDateInyyyymmdd(self.selectedDateInfo.fromDate)
        const response = yield getBulkSummary(
          mid,
          fileNameQuery,
          fromDate,
          toDate,
          pageNumber,
          pageSize,
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        console.log('fetchBulkSummary ', { response })
        const parsedData = response?.files
        own.summary = _isLoadMore
          ? JSON.parse(JSON.stringify([...own.summary, ...parsedData]))
          : JSON.parse(JSON.stringify(parsedData))
        if (pageNumber === 0) {
          own.pagination = JSON.parse(JSON.stringify(response?.page))
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
    }),

    getBulkRefundUploadConfirm: flow(function* getBulkRefundUploadConfirm(fileId: string) {
      let res
      try {
        res = yield bulkRefundUploadConfirmReq(fileId)
        console.log(res)
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
      return res
    }),

    uploadBulkRefundFile: flow(function* uploadBulkRefundFile(
      mid: string,
      fileName: string,
      fileSize: string,
      file: any,
    ) {
      let res
      try {
        res = yield uploadFileReq(mid, fileName, fileSize, RootStore.user.session.getTokens, file)
        const errorCode = res?.error?.code ?? ''
        console.log({ errorCode })
        if (errorCode === '33607' || errorCode === '33306') {
          if (errorCode === '33607') {
            extendUserSessionAPI().then((response) => {
              console.log('extendUserSessionAPI response', response)
              return (self as any).uploadBulkRefundFile(mid, fileName, fileSize, file)
            })
          } else {
            extendAppSessionAPI().then((response) => {
              console.log('extendAppSessionAPI response', response)
              return (self as any).uploadBulkRefundFile(mid, fileName, fileSize, file)
            })
          }
        }
        if (res?.status === 'FAILED') {
          // TODO: show error based on code
          self.uploadStatus = 'NOT_SELECTED'
          RootStore?.response?.setMessage(
            bulkRefundErrorMessage(
              res?.statusCode,
              res?.statusMessage ?? 'Invalid file selected, Please download sample file and upload in same format',
            ),
          )
        } else if (res?.status === 'INITIATED') {
          self.uploadedFileId = res?.id
          self.uploadStatus = 'UPLOADED'
          self.count = res?.count ?? null
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
      }
      return res
    }),

    downloadFileApi: flow(function* downloadFileApi(fileId: string, fileName: string) {
      let resp
      try {
        resp = yield bulkFileDownloadReq(
          APIEndPoints.exportBulkRefund + '?fileId=' + fileId,
          RootStore.user.session.getTokens,
        )
        const readableStream = resp.body
        let contentType = resp.headers.get('content-type')
        if (mimeTypes.get(contentType) === undefined) {
          resp = yield resp.json()
          if (resp.status === 'FAILURE' && resp.error.code === '33306') {
            yield extendAppSessionAPI()
            yield extendUserSessionAPI()
            return (self as any).downloadFileApi(fileId, fileName)
          } else if (resp.status === 'FAILURE' && resp.error.code === '33607') {
            yield extendUserSessionAPI()
            return (self as any).downloadFileApi(fileId, fileName)
          } else {
            RootStore?.response?.setMessage(resp.error?.message ?? customError.errorOccured)
          }
        } else {
          downloadFile(resp, fileName)
        }
      } catch (e) {
        console.log('error', e)
        RootStore?.response?.setMessage(e?.message ?? customError.errorOccured)
      }
      return resp
    }),

    bulkRefundProcess: flow(function* bulkRefundProcess(data, id) {
      const apiData =
        id === 'process'
          ? {
              excelEventId: data.file,
            }
          : {
              excelEventId: data.file,
              merchantId: data.merchantId,
            }
      try {
        const response = yield bulkRefundProcessReq(apiData, RootStore.user.session.userAccessToken, id)
        console.log(response)
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
    }),
    downloadSampleBulkRefundFile() {
      try {
        console.log('window?._env_?.REACT_APP_URL', window?._env_?.REACT_APP_URL)
        const appUrl = window?._env_?.REACT_APP_URL ? window?._env_?.REACT_APP_URL : 'http://10.157.255.94:9966'
        const fileName = 'jfs_yyyymmddhhmmss_mid_recordscount.csv'
        const url = `${appUrl}/${fileName}`
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = fileName
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (e) {
        console.log({ e })
      }
    },
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    resetPagination(): void {
      self.pagination = JSON.parse(JSON.stringify(paginationSettings))
    },
  }))
  .views((self) => ({
    getBulkSummaryData(): any[] {
      const trxn = bulkSummaryTableParse(self.summary)
      if (trxn.length === 0) {
        return []
      }
      return [...trxn]
    },
  }))

// const initialSummaryData = [
//   {
//     id: '1',
//     fileName: 'summary1.excel',
//     fileUploadDate: '12-03-2020 at 15:47:01',
//     count: 12,
//     totalRefundAmount: 400,
//     successCount: 6,
//     successAmount: 130,
//     failureCount: 6,
//     failureAmount: 270,
//     status: 'Complete',
//     action: 'Download',
//   },
//   {
//     id: '2',
//     fileName: 'summary2.excel',
//     fileUploadDate: '1-03-2020 at 10:47:01',
//     count: 12,
//     totalRefundAmount: 500,
//     successCount: 2,
//     successAmount: 130,
//     failureCount: 10,
//     failureAmount: 370,
//     status: 'Pending',
//     action: 'Download',
//   },
// ]

const bulkRefundInitialData = BulkRefund.create({
  report: [],
  summary: [],
  pagination: paginationSettings,
  selectedDateInfo: {
    fromDate: getSelectedDate(0),
    toDate: new Date(Date.now()),
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
    dateLabel: 'Today',
  },
})

export { BulkRefund, bulkRefundInitialData }
